import { useState, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Plus, MessageCircle, CalendarClock, Loader2, MapPin, Map, Copy, StickyNote, Route, Search, Trash2, Pencil, FlaskConical, RotateCcw, ArrowUp, ArrowDown, Bell, BellOff } from "lucide-react";
import { ownerApi } from "@/lib/api";
import { money, fmtSlotDate, itemLabel, itemLine } from "@/lib/format";
import { STATUS_META, flowFor } from "@/lib/status";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Spinner, EmptyState } from "@/components/common/States";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useShop } from "@/context/ShopContext";
import { leafletHtml, orderPickupsByRoad, googleRouteUrl, mapsDirUrl } from "@/lib/pickupRoute";
import { cn } from "@/lib/utils";

const FILTERS = ["all", "pending_approval", "placed", "confirmed", "picked_up", "in_cleaning", "ready", "out_for_delivery", "completed", "cancelled"];
const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "amount_high", label: "Amount ↑" },
  { key: "amount_low", label: "Amount ↓" },
  { key: "name", label: "Name A–Z" },
];
const todayISO = () => new Date().toISOString().slice(0, 10);

function waLink(b) {
  const digits = String(b.phone || "").replace(/\D/g, "");
  const wa = digits.length === 10 ? `91${digits}` : digits;
  const msg = `Hi ${b.customer_name}, update on your Dry Cleaners Goldys order ${b.code}: status is "${STATUS_META[b.status]?.label || b.status}". Total: ${money(b.total)}. Thank you!`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
}

// Asset-free chime via the Web Audio API — a short two-tone beep used for the new-order alert.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1175].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = freq;
      const t0 = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.35, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
      o.start(t0);
      o.stop(t0 + 0.34);
    });
    setTimeout(() => ctx.close(), 900);
  } catch { /* ignore */ }
}

// Detects orders arriving while the dashboard is open and fires a repeating chime until acknowledged.
function useNewOrderAlert(bookings) {
  const knownRef = useRef(null);
  const [alertOrder, setAlertOrder] = useState(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("goldys.owner_alert_muted") === "true");
  const toggleMuted = () => setMuted((m) => { const n = !m; localStorage.setItem("goldys.owner_alert_muted", String(n)); return n; });

  useEffect(() => {
    if (!bookings) return;
    const codes = new Set(bookings.map((b) => b.code));
    if (knownRef.current === null) { knownRef.current = codes; return; }
    const fresh = bookings.find((b) => !knownRef.current.has(b.code));
    knownRef.current = codes;
    if (fresh) setAlertOrder(fresh);
  }, [bookings]);

  useEffect(() => {
    if (!alertOrder || muted) return;
    playChime();
    const t = setInterval(playChime, 4000);
    return () => clearInterval(t);
  }, [alertOrder, muted]);

  return { alertOrder, dismiss: () => setAlertOrder(null), muted, toggleMuted };
}

function Metrics({ bookings }) {
  const all = (bookings ?? []).filter((b) => !b.is_test);
  const today = todayISO();
  const pickupsToday = all.filter((b) => b.mode === "pickup" && b.slot_date === today && b.status !== "cancelled").length;
  const inCleaning = all.filter((b) => b.status === "in_cleaning").length;
  const revenue = all.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.total || 0), 0);
  const rated = all.filter((b) => b.rating);
  const avgRating = rated.length ? (rated.reduce((s, b) => s + b.rating, 0) / rated.length).toFixed(1) : "—";
  const cards = [
    ["Today's pickups", pickupsToday],
    ["In cleaning", inCleaning],
    ["Revenue (paid)", money(revenue)],
    ["Avg rating", avgRating === "—" ? "—" : `${avgRating}★`],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="owner-metrics">
      {cards.map(([label, val]) => (
        <div key={label} className="rounded-2xl border border-border bg-card p-3">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-display text-lg font-bold">{val}</p>
        </div>
      ))}
    </div>
  );
}

export default function OwnerOrders() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [dateMode, setDateMode] = useState("any"); // any | day | month
  const [dateVal, setDateVal] = useState("");
  const [testView, setTestView] = useState("all"); // all | hide | only
  const [showBin, setShowBin] = useState(false);
  const [finalising, setFinalising] = useState(null);
  const [editing, setEditing] = useState(null);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [routeOpen, setRouteOpen] = useState(false);

  const query = useQuery({ queryKey: ["ownerBookings", "all"], queryFn: () => ownerApi.getBookings("all"), refetchInterval: 20000 });
  const all = useMemo(() => query.data ?? [], [query.data]);
  const alert = useNewOrderAlert(all);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ownerBookings"] });
  const act = (fn, msg) => fn.then(() => { invalidate(); toast.success(msg); }).catch((e) => toast.error(e.message || "Failed"));

  const visible = useMemo(() => {
    let list = all.slice();
    if (filter !== "all") list = list.filter((b) => b.status === filter);
    if (testView === "hide") list = list.filter((b) => !b.is_test);
    else if (testView === "only") list = list.filter((b) => b.is_test);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((b) => b.code.toLowerCase().includes(q) || (b.customer_name || "").toLowerCase().includes(q) || (b.phone || "").includes(q));
    }
    if (dateMode !== "any" && dateVal) {
      list = list.filter((b) => {
        const d = (b.created_at || "").slice(0, dateMode === "day" ? 10 : 7);
        return d === dateVal;
      });
    }
    const cmp = {
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      amount_high: (a, b) => (b.total || 0) - (a.total || 0),
      amount_low: (a, b) => (a.total || 0) - (b.total || 0),
      name: (a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""),
    }[sort];
    return list.sort(cmp);
  }, [all, filter, testView, search, dateMode, dateVal, sort]);

  if (showBin) return <RecycleBin onBack={() => setShowBin(false)} />;

  return (
    <div className="space-y-4">
      {alert.alertOrder ? (
        <div data-testid="owner-new-order-alert" className="flex items-center gap-3 rounded-2xl border border-primary bg-primary/10 px-4 py-3">
          <Bell className="h-5 w-5 shrink-0 animate-pulse text-primary" />
          <p className="flex-1 text-sm font-semibold">New order {alert.alertOrder.code} from {alert.alertOrder.customer_name}</p>
          <button onClick={alert.dismiss} data-testid="owner-new-order-dismiss" className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Open / dismiss</button>
        </div>
      ) : null}

      <Metrics bookings={all} />

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={cn("whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium", filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>
              {f === "all" ? "All" : STATUS_META[f]?.label || f}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={alert.toggleMuted} data-testid="owner-alert-mute" title={alert.muted ? "Alerts muted" : "Alerts on"} className={cn("flex h-9 w-9 items-center justify-center rounded-full border border-border", alert.muted ? "text-muted-foreground" : "text-primary")}>
            {alert.muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          </button>
          <button onClick={() => setShowBin(true)} data-testid="owner-recyclebin-button" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold"><Trash2 className="h-3.5 w-3.5" /> Recycle Bin</button>
          <button onClick={() => setWalkInOpen(true)} data-testid="owner-walkin-button" className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Plus className="h-3.5 w-3.5" /> Walk-in</button>
        </div>
      </div>

      {/* Search / date / sort / test toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, name or phone…" data-testid="owner-order-search" className="w-full rounded-full border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary" />
        </div>
        <select value={dateMode} onChange={(e) => { setDateMode(e.target.value); setDateVal(""); }} data-testid="owner-order-datemode" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          <option value="any">Any date</option>
          <option value="day">A day</option>
          <option value="month">A month</option>
        </select>
        {dateMode === "day" ? <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="owner-order-date" className="rounded-full border border-border bg-background px-3 py-2 text-xs" /> : null}
        {dateMode === "month" ? <input type="month" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="owner-order-month" className="rounded-full border border-border bg-background px-3 py-2 text-xs" /> : null}
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="owner-order-sort" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={testView} onChange={(e) => setTestView(e.target.value)} data-testid="owner-order-testview" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          <option value="all">Test: show</option>
          <option value="hide">Test: hide</option>
          <option value="only">Test: only</option>
        </select>
      </div>

      <button onClick={() => setRouteOpen(true)} data-testid="owner-route-button" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold">
        <Route className="h-4 w-4 text-primary" /> Today&apos;s pickups route
      </button>

      {query.isPending ? <Spinner /> : !visible.length ? <EmptyState title="No orders" hint="Try clearing the search or filters." /> : (
        <div className="grid gap-3 lg:grid-cols-2">
          {visible.map((b) => {
            const flow = flowFor(b.mode);
            const canEdit = b.status !== "cancelled" && b.payment_status !== "paid";
            return (
              <div key={b.id} data-testid={`owner-order-${b.code}`} className={cn("rounded-2xl border bg-card p-4", b.is_test ? "border-amber-400" : "border-border")}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-semibold">
                      {b.code} · {b.customer_name}
                      {b.is_test ? <span data-testid={`owner-test-tag-${b.code}`} className="ml-2 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-amber-950">TEST</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">{b.phone} · {fmtSlotDate(b.slot_date)} {b.slot_time} · {b.mode}</p>
                  </div>
                  <StatusBadge status={b.status} mode={b.mode} />
                </div>
                <p className="mt-2 text-sm text-foreground/70">{b.items.map((i) => (b.hide_quantities ? itemLabel(i) : `${i.qty}× ${itemLabel(i)}`)).join(", ")}{b.hide_quantities ? <span className="ml-1 text-[10px] text-muted-foreground">(qty hidden from customer)</span> : null}</p>
                {b.mode === "pickup" && (b.address || b.location) ? (
                  <div className="mt-2 rounded-xl bg-secondary p-3" data-testid={`owner-pickup-location-${b.code}`}>
                    {b.location?.lat != null && b.location?.lng != null ? (
                      <a href={mapsDirUrl(b.location.lat, b.location.lng)} target="_blank" rel="noopener noreferrer" data-testid={`owner-map-thumb-${b.code}`} className="relative mb-2 block h-24 w-full overflow-hidden rounded-lg border border-border">
                        <iframe title={`map-${b.code}`} srcDoc={leafletHtml([{ lat: b.location.lat, lng: b.location.lng }])} className="pointer-events-none h-full w-full" />
                        <span className="absolute inset-0" />
                      </a>
                    ) : null}
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        {b.address ? <p className="text-xs text-foreground/80 break-words">{b.address}</p> : null}
                        {b.pickup_distance_km > 0 ? <p className="text-[11px] text-muted-foreground">~{b.pickup_distance_km.toFixed(1)} km away</p> : null}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {b.address ? (
                        <button onClick={() => { navigator.clipboard?.writeText(b.address); toast.success("Address copied"); }} data-testid={`owner-copy-address-${b.code}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold">
                          <Copy className="h-3.5 w-3.5" /> Copy address
                        </button>
                      ) : null}
                      {b.location?.lat != null && b.location?.lng != null ? (
                        <a href={mapsDirUrl(b.location.lat, b.location.lng)} target="_blank" rel="noopener noreferrer" data-testid={`owner-open-maps-${b.code}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
                          <Map className="h-3.5 w-3.5" /> Open in Maps
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {b.notes ? (
                  <div className="mt-2 flex items-start gap-2 rounded-xl bg-[hsl(var(--brand-tertiary))] px-3 py-2" data-testid={`owner-notes-${b.code}`}>
                    <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <p className="text-xs text-foreground/80 break-words">{b.notes}</p>
                  </div>
                ) : null}
                {b.expected_delivery_date ? <p className="mt-1 text-xs text-muted-foreground">Est. delivery: {fmtSlotDate(b.expected_delivery_date)}</p> : null}
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-display font-bold text-primary">{money(b.total)}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-xs", b.payment_status === "paid" ? "bg-[hsl(var(--success)/0.14)] text-[hsl(142_60%_32%)]" : "bg-secondary text-muted-foreground")}>{b.payment_status}</span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {b.status === "pending_approval" ? (
                    <>
                      <button onClick={() => act(ownerApi.approveBooking(b.id), "Approved")} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Check className="h-3 w-3" /> Approve</button>
                      <button onClick={() => act(ownerApi.declineBooking(b.id, ""), "Declined")} className="inline-flex items-center gap-1 rounded-full border border-destructive px-3 py-1.5 text-xs font-semibold text-destructive"><X className="h-3 w-3" /> Decline</button>
                    </>
                  ) : null}
                  {b.status !== "cancelled" && b.status !== "pending_approval" ? (
                    <select value={b.status} onChange={(e) => act(ownerApi.updateStatus(b.id, e.target.value), "Status updated")} data-testid={`owner-status-${b.code}`} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium">
                      {flow.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                      <option value="cancelled">Cancelled</option>
                    </select>
                  ) : null}
                  {canEdit ? (
                    <button onClick={() => setEditing(b)} data-testid={`owner-edit-${b.code}`} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold"><Pencil className="h-3 w-3" /> Edit order</button>
                  ) : null}
                  {b.bill_status !== "final" && b.status !== "cancelled" ? (
                    <button onClick={() => setFinalising(b)} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold">Finalise bill</button>
                  ) : null}
                  {b.payment_status !== "paid" && b.total > 0 ? (
                    <select defaultValue="" onChange={(e) => e.target.value && act(ownerApi.markPaid(b.id, e.target.value), "Marked paid")} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium">
                      <option value="">Mark paid…</option>
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                    </select>
                  ) : null}
                  {b.status !== "cancelled" ? (
                    <button onClick={() => setEditingDate(b)} data-testid={`owner-setdate-${b.code}`} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold"><CalendarClock className="h-3 w-3" /> Est. date</button>
                  ) : null}
                  <button onClick={() => act(ownerApi.setTestFlag(b.id, !b.is_test), b.is_test ? "Unmarked test" : "Marked as test")} data-testid={`owner-test-toggle-${b.code}`} className={cn("inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold", b.is_test ? "border-amber-400 bg-amber-400 text-amber-950" : "border-border")}><FlaskConical className="h-3 w-3" /> {b.is_test ? "Test ✓" : "Test order"}</button>
                  <a href={waLink(b)} target="_blank" rel="noreferrer" data-testid={`owner-whatsapp-${b.code}`} className="inline-flex items-center gap-1 rounded-full border border-[hsl(142_70%_40%)] px-3 py-1.5 text-xs font-semibold text-[hsl(142_60%_32%)]"><MessageCircle className="h-3 w-3" /> WhatsApp</a>
                  <button onClick={() => { if (window.confirm(`Move ${b.code} to Recycle Bin?`)) act(ownerApi.deleteBooking(b.id), "Moved to Recycle Bin"); }} data-testid={`owner-delete-${b.code}`} className="inline-flex items-center gap-1 rounded-full border border-destructive px-3 py-1.5 text-xs font-semibold text-destructive"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {finalising ? <FinaliseDialog booking={finalising} onClose={() => setFinalising(null)} onDone={() => { setFinalising(null); invalidate(); }} /> : null}
      {editing ? <EditOrderDialog booking={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); invalidate(); }} /> : null}
      {walkInOpen ? <WalkInDialog onClose={() => setWalkInOpen(false)} onDone={() => { setWalkInOpen(false); invalidate(); }} /> : null}
      {editingDate ? <DeliveryDateDialog booking={editingDate} onClose={() => setEditingDate(null)} onDone={() => { setEditingDate(null); invalidate(); }} /> : null}
      {routeOpen ? <RouteDialog onClose={() => setRouteOpen(false)} /> : null}
    </div>
  );
}

function RecycleBin({ onBack }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerDeletedBookings"], queryFn: ownerApi.getDeletedBookings });
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ["ownerDeletedBookings"] }); queryClient.invalidateQueries({ queryKey: ["ownerBookings"] }); };
  const act = (fn, msg) => fn.then(() => { invalidate(); toast.success(msg); }).catch((e) => toast.error(e.message || "Failed"));
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="owner-recyclebin-back" className="rounded-full border border-border px-4 py-2 text-xs font-semibold">← Back to orders</button>
        <h2 className="font-display text-lg font-bold">Recycle Bin</h2>
      </div>
      <p className="text-sm text-muted-foreground">Deleted orders stay here until you permanently delete them.</p>
      {query.isPending ? <Spinner /> : !query.data?.length ? <EmptyState title="Recycle Bin is empty" hint="Deleted orders will appear here." /> : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="owner-recyclebin-list">
          {query.data.map((b) => (
            <div key={b.id} data-testid={`owner-deleted-${b.code}`} className="rounded-2xl border border-border bg-card p-4">
              <p className="font-display font-semibold">{b.code} · {b.customer_name}</p>
              <p className="text-xs text-muted-foreground">{b.phone} · {fmtSlotDate(b.slot_date)} · {money(b.total)}</p>
              <p className="mt-2 text-sm text-foreground/70">{b.items.map((i) => `${i.qty}× ${itemLabel(i)}`).join(", ")}</p>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => act(ownerApi.restoreBooking(b.id), "Order restored")} data-testid={`owner-restore-${b.code}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><RotateCcw className="h-3 w-3" /> Restore</button>
                <button onClick={() => { if (window.confirm(`Permanently delete ${b.code}? This cannot be undone.`)) act(ownerApi.deleteBookingPermanent(b.id), "Permanently deleted"); }} data-testid={`owner-perma-delete-${b.code}`} className="inline-flex items-center gap-1 rounded-full border border-destructive px-3 py-1.5 text-xs font-semibold text-destructive"><Trash2 className="h-3 w-3" /> Delete permanently</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditOrderDialog({ booking, onClose, onDone }) {
  const [items, setItems] = useState(booking.items.map((i) => ({ ...i, display_name: i.display_name || "" })));
  const [hideQty, setHideQty] = useState(!!booking.hide_quantities);
  const save = useMutation({
    mutationFn: () => ownerApi.editOrder(booking.id, items.map((i) => ({ service_id: i.service_id, qty: Number(i.qty), display_name: i.display_name || "" })), hideQty),
    onSuccess: () => { toast.success("Order updated"); onDone(); },
    onError: (e) => toast.error(e.message || "Could not update"),
  });
  const removeItem = (idx) => setItems((p) => p.filter((_, j) => j !== idx));
  const total = items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.qty) || 0), 0);
  const canSave = items.length > 0 && items.every((i) => Number(i.qty) > 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader><DialogTitle>Edit order · {booking.code}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Change quantities, remove or rename a line. This updates what the customer sees. Prices stay as set — use Finalise bill to change prices.</p>
        <div className="mt-2 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border border-border p-3" data-testid={`owner-edit-item-${it.service_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{it.name} · {money(it.price)}</span>
                <button onClick={() => removeItem(i)} data-testid={`owner-edit-remove-${it.service_id}`} className="rounded-full p-1 text-destructive hover:bg-destructive/10"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button onClick={() => setItems((p) => p.map((x, j) => j === i ? { ...x, qty: Math.max(0.5, Number(x.qty) - 1) } : x))} className="h-8 w-8 rounded-full bg-secondary font-bold">−</button>
                <input type="number" value={it.qty} onChange={(e) => setItems((p) => p.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} data-testid={`owner-edit-qty-${it.service_id}`} className="w-16 rounded-lg border border-border p-2 text-center text-sm" />
                <button onClick={() => setItems((p) => p.map((x, j) => j === i ? { ...x, qty: Number(x.qty) + 1 } : x))} className="h-8 w-8 rounded-full bg-secondary font-bold">+</button>
              </div>
              <input value={it.display_name} onChange={(e) => setItems((p) => p.map((x, j) => j === i ? { ...x, display_name: e.target.value } : x))} placeholder={`Customer label (default: ${it.name})`} data-testid={`owner-edit-label-${it.service_id}`} className="mt-2 w-full rounded-lg border border-border bg-background p-2 text-sm" />
            </div>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={hideQty} onChange={(e) => setHideQty(e.target.checked)} data-testid="owner-edit-hideqty" className="h-4 w-4" />
          Hide quantities from customer
        </label>
        <div className="mt-3 flex items-center justify-between text-sm font-semibold"><span>Items total</span><span className="text-primary">{money(total)}</span></div>
        <button onClick={() => save.mutate()} disabled={!canSave || save.isPending} data-testid="owner-edit-save" className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{save.isPending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Save changes"}</button>
      </DialogContent>
    </Dialog>
  );
}

function RouteDialog({ onClose }) {
  const { shop } = useShop();
  const today = todayISO();
  const query = useQuery({ queryKey: ["ownerBookings", "all"], queryFn: () => ownerApi.getBookings("all") });
  const shopLoc = shop?.shop_lat != null && shop?.shop_lng != null ? { lat: shop.shop_lat, lng: shop.shop_lng } : null;
  const pickups = (query.data ?? []).filter((b) => b.mode === "pickup" && b.slot_date === today && b.status !== "cancelled" && b.status !== "completed" && !b.is_test);
  const withPin = pickups.filter((b) => b.location?.lat != null && b.location?.lng != null);
  const noPin = pickups.filter((b) => !(b.location?.lat != null && b.location?.lng != null));

  const stops = useMemo(
    () => withPin.map((b) => ({ code: b.code, name: b.customer_name, location: b.location })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [withPin.map((b) => b.code).join(",")],
  );
  const collectedKey = `goldys.pickups_collected.${today}`;
  const [collected, setCollected] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(collectedKey) || "[]")); } catch { return new Set(); }
  });
  const [rows, setRows] = useState([]);
  const [routing, setRouting] = useState(true);

  useEffect(() => {
    let alive = true;
    setRouting(true);
    orderPickupsByRoad(shopLoc, stops).then((o) => { if (alive) { setRows(o); setRouting(false); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops, shopLoc?.lat, shopLoc?.lng]);

  const toggleCollected = (code) => {
    setCollected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      try { localStorage.setItem(collectedKey, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };
  const move = (idx, dir) => {
    setRows((prev) => {
      const arr = prev.slice();
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const pending = rows.filter((s) => !collected.has(s.code));
  const done = rows.filter((s) => collected.has(s.code));
  const display = [...pending, ...done];
  const markers = [
    ...(shopLoc ? [{ ...shopLoc, label: "Shop" }] : []),
    ...pending.map((s, i) => ({ lat: s.location.lat, lng: s.location.lng, label: String(i + 1) })),
  ];
  const routeUrl = googleRouteUrl(shopLoc, pending);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Today&apos;s pickups · {pending.length} to go{done.length ? ` · ${done.length} collected` : ""}</DialogTitle></DialogHeader>
        {query.isPending ? <Spinner /> : pickups.length === 0 ? (
          <EmptyState title="No pickups today" hint="Doorstep pickups scheduled for today will appear here." />
        ) : (
          <div className="space-y-3">
            {routing ? <p className="text-xs text-muted-foreground" data-testid="owner-route-computing">Ordering stops by driving distance…</p> : null}
            {display.length ? (
              <div className="h-48 w-full overflow-hidden rounded-xl border border-border">
                <iframe title="pickup-route-map" srcDoc={leafletHtml(markers, { line: true })} className="h-full w-full" data-testid="owner-route-map" />
              </div>
            ) : null}
            {routeUrl ? (
              <a href={routeUrl} target="_blank" rel="noopener noreferrer" data-testid="owner-route-open-all" className="flex items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold text-primary-foreground">
                <Route className="h-4 w-4" /> Open full route in Google Maps
              </a>
            ) : null}
            <ol className="space-y-2" data-testid="owner-route-list">
              {display.map((s) => {
                const isDone = collected.has(s.code);
                const pIdx = pending.indexOf(s);
                return (
                  <li key={s.code} className={cn("flex items-center gap-2 rounded-xl px-3 py-2", isDone ? "bg-secondary/50 opacity-60" : "bg-secondary")}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{isDone ? "✓" : pIdx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className={cn("truncate text-sm font-medium", isDone && "line-through")}>{s.name} · {s.code}</p>
                      {!isDone ? <p className="text-[11px] text-muted-foreground">~{s.legKm.toFixed(1)} km from {pIdx === 0 ? "shop" : "previous"}</p> : null}
                    </div>
                    {!isDone ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => move(rows.indexOf(s), -1)} data-testid={`owner-route-up-${s.code}`} className="flex h-7 w-7 items-center justify-center rounded-full bg-card" aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => move(rows.indexOf(s), 1)} data-testid={`owner-route-down-${s.code}`} className="flex h-7 w-7 items-center justify-center rounded-full bg-card" aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                        <a href={mapsDirUrl(s.location.lat, s.location.lng)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-card px-2.5 py-1.5 text-xs font-semibold"><Map className="h-3.5 w-3.5 text-primary" /></a>
                      </div>
                    ) : null}
                    <button onClick={() => toggleCollected(s.code)} data-testid={`owner-route-collect-${s.code}`} className={cn("rounded-full px-3 py-1.5 text-xs font-semibold", isDone ? "bg-card text-muted-foreground" : "bg-primary text-primary-foreground")}>
                      {isDone ? "Undo" : "Collected"}
                    </button>
                  </li>
                );
              })}
            </ol>
            {noPin.length ? <p className="text-xs text-muted-foreground">{noPin.length} pickup{noPin.length === 1 ? "" : "s"} without a map pin aren&apos;t on the route: {noPin.map((b) => b.code).join(", ")}</p> : null}
            {!shopLoc ? <p className="text-xs text-muted-foreground">Set the shop location in Settings to order stops from the shop.</p> : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeliveryDateDialog({ booking, onClose, onDone }) {
  const [date, setDate] = useState(booking.expected_delivery_date || "");
  const save = useMutation({
    mutationFn: () => ownerApi.setDeliveryDate(booking.id, date),
    onSuccess: () => { toast.success("Delivery estimate updated"); onDone(); },
    onError: (e) => toast.error(e.message || "Could not update"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Expected delivery · {booking.code}</DialogTitle></DialogHeader>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="owner-delivery-date-input" className="w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary" />
        <button onClick={() => save.mutate()} disabled={save.isPending || !date} className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{save.isPending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Save estimate"}</button>
      </DialogContent>
    </Dialog>
  );
}

function WalkInDialog({ onClose, onDone }) {
  const servicesQuery = useQuery({ queryKey: ["ownerServicesWalkin"], queryFn: ownerApi.getServices });
  const services = (servicesQuery.data ?? []).filter((s) => s.active !== false && s.unit !== "call");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [cart, setCart] = useState({});
  const [search, setSearch] = useState("");
  const [openCats, setOpenCats] = useState({});
  const save = useMutation({
    mutationFn: () => ownerApi.createWalkIn({
      customer_name: name.trim(),
      phone: phone.replace(/\D/g, ""),
      items: Object.entries(cart).filter(([, q]) => q > 0).map(([service_id, qty]) => ({ service_id, qty: Number(qty) })),
      notes: notes.trim(),
    }),
    onSuccess: () => { toast.success("Walk-in order created"); onDone(); },
    onError: (e) => toast.error(e.message || "Could not create walk-in"),
  });
  const itemCount = Object.values(cart).reduce((a, b) => a + Number(b), 0);
  const q = search.trim().toLowerCase();
  const matched = q ? services.filter((s) => s.name.toLowerCase().includes(q)) : services;
  const grouped = useMemo(() => {
    const m = {};
    matched.forEach((s) => { (m[s.category] = m[s.category] || []).push(s); });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [matched]);
  const canSave = name.trim().length >= 2 && phone.replace(/\D/g, "").length >= 7 && itemCount > 0;
  const stepper = (s) => (
    <div key={s.service_id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2 text-sm" data-testid={`walkin-service-${s.service_id}`}>
      <span className="min-w-0 flex-1 truncate">{s.name} · {money(s.price)}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => setCart((c) => ({ ...c, [s.service_id]: Math.max(0, (Number(c[s.service_id]) || 0) - 1) }))} className="h-7 w-7 rounded-full bg-secondary font-bold">−</button>
        <span className="w-5 text-center">{cart[s.service_id] || 0}</span>
        <button onClick={() => setCart((c) => ({ ...c, [s.service_id]: (Number(c[s.service_id]) || 0) + 1 }))} className="h-7 w-7 rounded-full bg-secondary font-bold">+</button>
      </div>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-md overflow-y-auto">
        <DialogHeader><DialogTitle>New walk-in order</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" data-testid="walkin-name" className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" placeholder="Phone" data-testid="walkin-phone" className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services…" data-testid="walkin-search" className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" />
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {q ? (
              matched.length ? matched.map(stepper) : <p className="py-4 text-center text-sm text-muted-foreground">No services match “{search}”.</p>
            ) : (
              grouped.map(([cat, list]) => {
                const open = openCats[cat] ?? false;
                const picked = list.reduce((n, s) => n + (Number(cart[s.service_id]) || 0), 0);
                return (
                  <div key={cat} className="rounded-xl border border-border">
                    <button onClick={() => setOpenCats((o) => ({ ...o, [cat]: !open }))} data-testid={`walkin-cat-${cat}`} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold">
                      <span>{cat} <span className="text-xs font-normal text-muted-foreground">({list.length})</span></span>
                      <span className="flex items-center gap-2">{picked > 0 ? <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] text-primary-foreground">{picked}</span> : null}<span className="text-muted-foreground">{open ? "▾" : "▸"}</span></span>
                    </button>
                    {open ? <div className="space-y-1 p-2 pt-0">{list.map(stepper)}</div> : null}
                  </div>
                );
              })
            )}
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" />
        </div>
        <button onClick={() => save.mutate()} disabled={!canSave || save.isPending} data-testid="walkin-save" className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{save.isPending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : `Create order · ${itemCount} items`}</button>
      </DialogContent>
    </Dialog>
  );
}

function FinaliseDialog({ booking, onClose, onDone }) {
  const [items, setItems] = useState(booking.items.map((i) => ({ ...i })));
  const [extras, setExtras] = useState(booking.extra_charges ?? []);
  const save = useMutation({
    mutationFn: () => ownerApi.finaliseBill(booking.id, items.map((i) => ({ service_id: i.service_id, qty: Number(i.qty), price: Number(i.price) })), extras.filter((e) => e.label && e.amount).map((e) => ({ label: e.label, amount: Number(e.amount) }))),
    onSuccess: () => { toast.success("Bill finalised"); onDone(); },
    onError: (e) => toast.error(e.message || "Could not finalise"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader><DialogTitle>Finalise bill · {booking.code}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="flex-1 truncate text-sm">{it.name}</span>
              <input type="number" value={it.qty} onChange={(e) => setItems((p) => p.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} className="w-16 rounded-lg border border-border p-2 text-sm" />
              <input type="number" value={it.price} onChange={(e) => setItems((p) => p.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} className="w-20 rounded-lg border border-border p-2 text-sm" />
            </div>
          ))}
          <p className="pt-2 text-sm font-semibold">Extra charges</p>
          {extras.map((e, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={e.label} onChange={(ev) => setExtras((p) => p.map((x, j) => j === i ? { ...x, label: ev.target.value } : x))} placeholder="Label" className="flex-1 rounded-lg border border-border p-2 text-sm" />
              <input type="number" value={e.amount} onChange={(ev) => setExtras((p) => p.map((x, j) => j === i ? { ...x, amount: ev.target.value } : x))} placeholder="₹" className="w-20 rounded-lg border border-border p-2 text-sm" />
            </div>
          ))}
          <button onClick={() => setExtras((p) => [...p, { label: "", amount: "" }])} className="text-xs font-semibold text-primary">+ Add charge</button>
        </div>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Finalise &amp; notify customer</button>
      </DialogContent>
    </Dialog>
  );
}
